/**
 * Shared plumbing for the four play stages
 * (1 image quiz → 2 crossword → 3 audio quiz → 4 match pairs).
 *
 * `useStageRun(n)` handles the stage row bootstrap + "already played" redirect,
 * a timer and points total that continue across stages, theme colors/background,
 * and `finishStage()` which saves the stage and moves on (the last stage also
 * finalizes the run and opens the thank-you page).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { LOGIN_BACK_PATH } from "../gameSetupStorage";
import { getThemeButtonGradient, getThemeButtonTextColor } from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  TOTAL_GAME_STAGES,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getNextStageRoute,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  resolveRunProgress,
  saveGameStageAndReport,
  setStageActive,
  writeRunProgress,
} from "../../functions/stageReportSync";
import { getRunPlan, splitHighlight } from "./stageContent";
import "../ny.css";
import "./dragdrop.css";
import "./quiz.css";
import "./stages.css";

export const DEMO_PLANS_URL = "https://extramileplay.com/plans";

export function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** CSS variables for the theme's button colors (used by every player screen). */
export function buildAccentStyle(themeData) {
  const { start, end } = getThemeButtonGradient(themeData || {});
  const text = getThemeButtonTextColor(themeData || {});
  const style = {};
  if (start) {
    style["--ny-accent"] = start;
    style["--ny-accent-2"] = end || start;
  }
  if (text) style["--ny-accent-ink"] = text;
  return style;
}

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function parseAnswers(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Unlock document scroll while a stage is open (login CSS locks body overflow on mobile). */
function useStageScrollUnlock() {
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("dd-stage-open");
    html.classList.add("dd-stage-open");

    const apply = () => {
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");
      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("dd-stage-open");
      html.classList.remove("dd-stage-open");
      ["overflow", "overflow-x", "overflow-y", "height", "max-height"].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);
}

export function useStageRun(stageNumber) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const pointsPerCorrect = Math.max(
    1,
    parseInt(themeData?.correctPoints ?? themeData?.points, 10) || 1
  );
  const titleColor = String(themeData?.title_color || "#ffffff").trim();
  const plan = useMemo(() => getRunPlan(themeData), [themeData]);

  // Theme button colors (Rules page) drive buttons and letter badges.
  const accentStyle = useMemo(() => buildAccentStyle(themeData), [themeData]);

  const [dbReady, setDbReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );
  const [baseScore, setBaseScore] = useState(0);
  const [stageScore, setStageScore] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [timerOn, setTimerOn] = useState(false);

  const baseRef = useRef({ score: 0, seconds: 0 });
  const elapsedRef = useRef(0);
  const stageScoreRef = useRef(0);
  const priorAnswersRef = useRef([]);
  const finishingRef = useRef(false);

  useStageScrollUnlock();

  useEffect(() => {
    dispatch(setBackButtonUrl(LOGIN_BACK_PATH));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const applyBase = (record) => {
      // Stage 1 always starts a fresh run unless the DB says otherwise.
      const base =
        record || stageNumber > 1 ? resolveRunProgress(record) : { score: 0, seconds: 0 };
      baseRef.current = base;
      elapsedRef.current = base.seconds;
      priorAnswersRef.current = record ? parseAnswers(record.user_answers) : [];
      if (!cancelled) {
        setBaseScore(base.score);
        setElapsedSec(base.seconds);
      }
    };

    async function bootstrap() {
      setStageActive(stageNumber, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        applyBase(null);
        if (!cancelled) setDbReady(true);
        return;
      }
      let record = null;
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        record = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (record && typeof record === "object") {
          hydrateCompletedStagesFromRecord(record);
          const redirect = getRedirectForPlayedStage(record, stageNumber, {});
          if (redirect) {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error(`Stage ${stageNumber} bootstrap:`, err);
      }
      applyBase(record);
      if (!cancelled) setDbReady(true);
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, stageNumber, storedUser]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const startTimer = useCallback(() => setTimerOn(true), []);

  const addPoints = useCallback((points) => {
    stageScoreRef.current += Number(points) || 0;
    setStageScore(stageScoreRef.current);
  }, []);

  /**
   * Save this stage (score + answers) and continue. Call with no answers to skip a
   * stage that has no content configured — it is still recorded so resume works.
   */
  const finishStage = useCallback(
    async ({ answers = [] } = {}) => {
      if (finishingRef.current) return;
      finishingRef.current = true;
      setTimerOn(false);

      const total = baseRef.current.score + stageScoreRef.current;
      const seconds = elapsedRef.current;
      const time = formatClock(seconds);
      const isLast = stageNumber >= TOTAL_GAME_STAGES;
      writeRunProgress({ score: total, seconds });

      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const tagged = answers.map((a) => ({ ...a, stage: stageNumber }));
          const result = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
            stageNumber,
            score: stageScoreRef.current,
            total_score: total,
            time,
            gameover: isLast,
            // Last stage marks the whole run done in the stages table.
            ...(isLast ? { winner: "completed" } : {}),
            user_answers: [...priorAnswersRef.current, ...tagged],
          });
          if (result?.stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(result.stageId));
            setStageRowId(String(result.stageId));
          }
        }
      } catch (err) {
        console.error(`Stage ${stageNumber} save:`, err);
      }

      clearStageActive(stageNumber);
      markStageCompleted(stageNumber, storedUser);

      if (isLast && isDemoBypass) {
        window.location.href = DEMO_PLANS_URL;
        return;
      }
      if (isLast) {
        navigate("/thankyou", { replace: true, state: { points: total, time } });
        return;
      }
      navigate(getNextStageRoute(stageNumber), { replace: true });
    },
    [adminToken, backendBase, isDemoBypass, navigate, stageNumber, stageRowId, storedUser]
  );

  const isLoading = (themeStatus === "loading" && !themeData) || !dbReady;

  return {
    themeData,
    themeStatus,
    isLoading,
    isDemoBypass,
    pointsPerCorrect,
    titleColor,
    accentStyle,
    elapsedSec,
    totalScore: baseScore + stageScore,
    stageNumber,
    plan,
    stageScore,
    startTimer,
    addPoints,
    finishStage,
  };
}

export function StageLoader() {
  return (
    <div className="quiz-loader-container">
      <div className="quiz-loader" />
    </div>
  );
}

/** Title with "New Year" picked out in gold, as in the reference design. */
export function HighlightTitle({ text, as: Tag = "h1", className = "", style }) {
  return (
    <Tag className={className} style={style}>
      {splitHighlight(text).map((part, i) =>
        part.highlight ? (
          <span key={i} className="ny-gold-text">
            {part.text}
          </span>
        ) : (
          <React.Fragment key={i}>{part.text}</React.Fragment>
        )
      )}
    </Tag>
  );
}

/** Dots joined by a line + "3 / 10" — whole-run progress across all four stages. */
export function RunTracker({ current, total }) {
  if (!total) return null;
  const safeCurrent = Math.min(Math.max(current, 1), total);
  const showDots = total <= 12;
  return (
    <div className="ny-tracker" aria-label={`Step ${safeCurrent} of ${total}`}>
      {showDots ? (
        <ol className="ny-tracker-dots" aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <li
              key={i}
              className={`ny-tracker-dot${i < safeCurrent - 1 ? " is-done" : ""}${
                i === safeCurrent - 1 ? " is-current" : ""
              }`}
            />
          ))}
        </ol>
      ) : (
        <span className="ny-tracker-bar" aria-hidden>
          <span style={{ width: `${(safeCurrent / total) * 100}%` }} />
        </span>
      )}
      <span className="ny-tracker-count">
        {safeCurrent} / {total}
      </span>
    </div>
  );
}

/** "Time: 00:19" (left) and "Points: 3" (right) pills. */
export function StagePill({ kind, children }) {
  return (
    <div className={`ny-pill ny-pill--${kind}`}>
      <i
        className={kind === "time" ? "fa-regular fa-clock" : "fa-solid fa-star"}
        aria-hidden
      />
      <span>{children}</span>
    </div>
  );
}

/**
 * Full-page stage frame: admin background, progress tracker, then a row of
 * [Time pill] [title + subtitle] [Points pill], then the stage content.
 * `step` is this screen's 0-based position within the stage.
 */
export function StageShell({ run, heading, subheading, step = 0, children, className = "" }) {
  const current = run.plan.stageOffsets[run.stageNumber - 1] + step + 1;
  return (
    // The stage background image is painted on <body> (pages/dddUI/user.jsx), not here.
    <div className={`ny-page ny-stage ${className}`.trim()} style={run.accentStyle}>
      <RunTracker current={current} total={run.plan.totalSteps} />

      <div className="ny-headrow">
        <StagePill kind="time">Time: {formatClock(run.elapsedSec)}</StagePill>
        <header className="ny-heading">
          <HighlightTitle text={heading} className="ny-title" style={{ color: run.titleColor }} />
          {subheading ? (
            <p className="ny-subtitle" style={{ color: run.titleColor }}>
              {subheading}
            </p>
          ) : null}
        </header>
        <StagePill kind="points">Points: {Math.max(0, Math.floor(run.totalScore))}</StagePill>
      </div>

      {children}

      <div className="dd-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}

export function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

